import { useState } from 'react';
import { PrimaryButton, TextButton } from '../components/Button';
import { Card } from '../components/Card';
import { Screen } from '../components/Screen';
import { StatusChip, StatusRing, type RingState } from '../components/Status';
import { copy } from '../copy';
import { delayMinutes, formatClock } from '../lib/time';
import { isStopHandled, stopCounts, visibleStops } from '../state/route';
import { selectCurrentStop, selectQueueCount, useAppStore } from '../state/store';
import type { DriverStop } from '../api/types';

const COLLAPSE_THRESHOLD = 8;

function ringStateFor(stop: DriverStop, isCurrent: boolean): RingState {
  if (stop.status === 'delivered') return 'delivered';
  if (stop.status === 'failed' || stop.status === 'redeliveryPlanned') return 'failed';
  if (isCurrent) return 'current';
  return 'open';
}

function ringLabelFor(stop: DriverStop, isCurrent: boolean): string {
  if (stop.status === 'delivered') return `stop ${stop.stopOrder}, bezorgd`;
  if (stop.status === 'failed' || stop.status === 'redeliveryPlanned') {
    return `stop ${stop.stopOrder}, niet gelukt`;
  }
  return isCurrent ? `stop ${stop.stopOrder}, nu naartoe` : `stop ${stop.stopOrder}, open`;
}

/** E1/E5/E6/E7 — De rit. Afgehandelde stops blijven zichtbaar, in volgorde. */
export function RideScreen() {
  const route = useAppStore((s) => s.route);
  const online = useAppStore((s) => s.online);
  const queueCount = useAppStore(selectQueueCount);
  const current = useAppStore(selectCurrentStop);
  const navigate = useAppStore((s) => s.navigate);
  const openSheet = useAppStore((s) => s.openSheet);
  const [expanded, setExpanded] = useState(false);

  if (!route) return null;

  const stops = visibleStops(route);
  const counts = stopCounts(route);
  const handled = stops.filter((stop) => isStopHandled(stop.status));
  const collapse = !expanded && stops.length > COLLAPSE_THRESHOLD && handled.length > 0;
  const listed = collapse ? stops.filter((stop) => !isStopHandled(stop.status)) : stops;
  const delay = current ? delayMinutes(current.plannedArrivalAt) : null;

  return (
    <Screen
      name="ride"
      footDivided
      foot={
        current ? (
          <>
            <PrimaryButton onClick={() => navigate('stop', current.id)}>
              {copy.ride.openStop(current.stopOrder)}
            </PrimaryButton>
            {/*
              D138: de rit moet ook af te ronden zijn terwijl er nog stops open staan (B4).
              Tekstknop, geen tweede primaire actie — de duimzone-regel blijft dus staan.
            */}
            <TextButton variant="muted" center onClick={() => openSheet('complete')}>
              {copy.ret.complete}
            </TextButton>
          </>
        ) : (
          <PrimaryButton onClick={() => navigate('return')}>{copy.ret.title}</PrimaryButton>
        )
      }
    >
      <div className="pad-x" style={{ paddingTop: 14 }}>
        <div className="row row--baseline">
          <h1 className="t-serif t-titel">{copy.ride.title}</h1>
          <span className="t-caption o-65">{copy.ride.progress(counts.delivered, counts.total)}</span>
        </div>
        {!online && queueCount > 0 ? (
          <div style={{ paddingTop: 10 }}>
            <StatusChip tone="waiting">{copy.status.queue(queueCount)}</StatusChip>
          </div>
        ) : null}
      </div>

      {current ? (
        <div className="pad-x" style={{ paddingTop: 14 }}>
          <Card variant="current">
            <div className="row" style={{ gap: 12, alignItems: 'center' }}>
              <StatusRing
                state="current"
                size={34}
                label={current.stopOrder}
                ariaLabel={ringLabelFor(current, true)}
              />
              <span className="t-caption o-65">
                {copy.ride.current(formatClock(current.plannedArrivalAt))}
                {delay !== null ? ` ${copy.ride.delay(delay)}` : ''}
              </span>
            </div>
            <div style={{ fontSize: 23, fontWeight: 600, marginTop: 12 }}>{current.partnerName}</div>
            <div style={{ fontSize: 20, marginTop: 4 }}>
              {current.street}
              <br />
              {current.postalCodeCity}
            </div>
          </Card>
        </div>
      ) : null}

      <div className="pad-x" style={{ paddingTop: 12, paddingBottom: 8 }}>
        {collapse ? (
          <TextButton onClick={() => setExpanded(true)}>
            {copy.ride.collapsed(handled.length)}
          </TextButton>
        ) : null}
        {listed.map((stop) => {
          const isCurrent = current?.id === stop.id;
          const done = isStopHandled(stop.status);
          return (
            <button
              key={stop.id}
              type="button"
              className="listrow listrow--plain"
              onClick={() => navigate('stop', stop.id)}
            >
              <StatusRing
                state={ringStateFor(stop, isCurrent)}
                size={30}
                label={done ? undefined : stop.stopOrder}
                ariaLabel={ringLabelFor(stop, isCurrent)}
              />
              <span className={`listrow__label${done ? ' listrow__label--done' : ''}`}>
                {copy.load.destination(
                  stop.partnerName,
                  stop.postalCodeCity.split(' ').slice(2).join(' ') || stop.postalCodeCity,
                )}
              </span>
              <span className="listrow__time">
                {formatClock(stop.deliveredAt ?? stop.arrivedAt ?? stop.plannedArrivalAt)}
              </span>
            </button>
          );
        })}
      </div>
    </Screen>
  );
}
